import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Minus, Plus, Scan, Tags } from 'lucide-react';
import { hexAlpha, isHexColor, readableOn } from '../../../components/ui';
import { InfluenceEntity, UNASSESSED_REGION } from './influenceModel';
import {
  Camera,
  clampZoom,
  easeOutCubic,
  fitCamera,
  interpolateCamera,
  LayoutInput,
  layoutPowerMap,
  MapLayout,
  MapRegion,
} from './powerMapLayout';
import { areaColor } from './ui';

export interface PowerMapHandle {
  fit: () => void;
  focusEntity: (id: string) => void;
  /** Frames every instance of these entities */
  focusEntities: (ids: string[]) => void;
  focusRegion: (id: string) => void;
}

export interface RegionMeta {
  /** e.g. "Concentrated · 12 scored" */
  sub: string;
  /** shares (0–1) of the top actors, drawn as the region's outer ring */
  shares: number[];
}

interface PowerMapProps {
  input: Omit<LayoutInput, 'aspect'>;
  entityById: Map<string, InfluenceEntity>;
  regionMeta: Record<string, RegionMeta>;
  selectedId: string | null;
  hoverId: string | null;
  compareIds: string[];
  highlightIds: string[];
  /** Search matches; others fade */
  matchIds: Set<string> | null;
  /** Direct network ties of the selected entity */
  ties: string[];
  active: boolean;
  reducedMotion: boolean;
  /** Lift the camera controls above a bottom dock (phones) */
  dockSpace?: boolean;
  onHover: (id: string | null) => void;
  onSelect: (id: string | null) => void;
  onOpen: (id: string) => void;
}

interface Pose {
  x: number;
  y: number;
  r: number;
  a: number;
}

interface AnimNode {
  key: string;
  entityId: string;
  regionId: string;
  score: number;
  color: string | null;
  from: Pose;
  to: Pose;
  start: number;
  delay: number;
  dur: number;
  leaving: boolean;
}

interface RegionPose {
  cx: number;
  cy: number;
  R: number;
  scaleR: number;
  a: number;
}

interface AnimRegion {
  region: MapRegion;
  from: RegionPose;
  to: RegionPose;
  start: number;
  dur: number;
  leaving: boolean;
}

const FONT = 'Geist, "Plus Jakarta Sans", system-ui, sans-serif';
const ACCENT_RGB = '234, 42, 51';
const NODE_DUR = 760;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

function nodePose(n: AnimNode, now: number): Pose {
  const t = Math.max(0, Math.min(1, (now - n.start - n.delay) / n.dur));
  const e = easeOutCubic(t);
  return { x: lerp(n.from.x, n.to.x, e), y: lerp(n.from.y, n.to.y, e), r: lerp(n.from.r, n.to.r, e), a: lerp(n.from.a, n.to.a, e) };
}

function regionPose(r: AnimRegion, now: number): RegionPose {
  const t = Math.max(0, Math.min(1, (now - r.start) / r.dur));
  const e = easeOutCubic(t);
  return {
    cx: lerp(r.from.cx, r.to.cx, e),
    cy: lerp(r.from.cy, r.to.cy, e),
    R: lerp(r.from.R, r.to.R, e),
    scaleR: lerp(r.from.scaleR, r.to.scaleR, e),
    a: lerp(r.from.a, r.to.a, e),
  };
}

function rgbOf(hex: string): [number, number, number] {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Solid mix towards white; low scores read paler */
function nodeFill(color: string | null, score: number, unassessed: boolean): [number, number, number] {
  if (unassessed) {
    const v = Math.round(229 - (score / 100) * 66); // neutral-200 → neutral-400
    return [v, v, v];
  }
  const [r, g, b] = rgbOf(isHexColor(color) ? color : '#a3a3a3');
  const amount = 0.42 + (0.58 * Math.max(0, Math.min(100, score))) / 100;
  const mix = (c: number) => Math.round(255 + (c - 255) * amount);
  return [mix(r), mix(g), mix(b)];
}

const luminance = ([r, g, b]: [number, number, number]) => (0.299 * r + 0.587 * g + 0.114 * b) / 255;

function shapePath(ctx: CanvasRenderingContext2D, org: boolean, x: number, y: number, radius: number) {
  // Entering/leaving nodes animate through r = 0; canvas throws on negative radii
  const r = Math.max(0, radius);
  ctx.beginPath();
  if (org) {
    const s = r * 0.9;
    const rad = s * 0.38;
    ctx.moveTo(x - s + rad, y - s);
    ctx.arcTo(x + s, y - s, x + s, y + s, rad);
    ctx.arcTo(x + s, y + s, x - s, y + s, rad);
    ctx.arcTo(x - s, y + s, x - s, y - s, rad);
    ctx.arcTo(x - s, y - s, x + s, y - s, rad);
    ctx.closePath();
  } else {
    ctx.arc(x, y, r, 0, Math.PI * 2);
  }
}

type CameraIntent = { kind: 'fit' } | { kind: 'entities'; ids: string[] } | { kind: 'region'; id: string } | null;

const truncate = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

export const PowerMap = forwardRef<PowerMapHandle, PowerMapProps>(function PowerMap(props, ref) {
  const {
    input,
    entityById,
    regionMeta,
    selectedId,
    hoverId,
    compareIds,
    highlightIds,
    matchIds,
    ties,
    active,
    dockSpace = false,
    onHover,
    onSelect,
    onOpen,
  } = props;

  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [showLabels, setShowLabels] = useState(true);
  const [card, setCard] = useState<{ id: string; regionId: string; sx: number; sy: number } | null>(null);

  const cameraRef = useRef<Camera>({ x: 0, y: 0, k: 1 });
  const flightRef = useRef<{ from: Camera; to: Camera; start: number; dur: number } | null>(null);
  const nodesRef = useRef<Map<string, AnimNode>>(new Map());
  const regionsRef = useRef<Map<string, AnimRegion>>(new Map());
  const layoutRef = useRef<MapLayout | null>(null);
  const frameRef = useRef<number | null>(null);
  const highlightAtRef = useRef(0);
  /** What the camera is framing, so it can be re-framed when the viewport resizes; null after manual moves */
  const intentRef = useRef<CameraIntent>({ kind: 'fit' });
  const labelWidthCache = useRef<Map<string, number>>(new Map());

  // Latest props for the draw loop without re-creating it
  const live = useRef(props);
  live.current = props;
  const labelsRef = useRef(showLabels);
  labelsRef.current = showLabels;

  /* ---------- size ---------- */
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setSize((s) => (s.w === el.clientWidth && s.h === el.clientHeight ? s : { w: el.clientWidth, h: el.clientHeight }));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Layout arranges regions for the viewport shape; bucket the aspect so small resizes don't reshuffle
  const aspect = size.w && size.h ? Math.round((size.w / size.h) * 4) / 4 : 0;
  const layout = useMemo(() => (aspect ? layoutPowerMap({ ...input, aspect }) : null), [input, aspect]);

  /* ---------- render loop ---------- */
  const draw = useCallback(() => {
    frameRef.current = null;
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext ? canvas.getContext('2d') : null;
    if (!ctx) return;
    const p = live.current;
    const w = wrap.clientWidth;
    const h = wrap.clientHeight;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }
    const now = performance.now();
    let animating = false;

    const flight = flightRef.current;
    if (flight) {
      const t = Math.min(1, (now - flight.start) / flight.dur);
      cameraRef.current = interpolateCamera(flight.from, flight.to, t, Math.min(w, h));
      if (t >= 1) {
        cameraRef.current = flight.to;
        flightRef.current = null;
      } else animating = true;
    }
    const cam = cameraRef.current;
    const k = cam.k;
    const sx = (x: number) => (x - cam.x) * k + w / 2;
    const sy = (y: number) => (y - cam.y) * k + h / 2;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    /* regions */
    const regionPoses: Array<{ region: MapRegion; pose: RegionPose }> = [];
    regionsRef.current.forEach((r, id) => {
      const pose = regionPose(r, now);
      if (now - r.start < r.dur) animating = true;
      else if (r.leaving) {
        regionsRef.current.delete(id);
        return;
      }
      regionPoses.push({ region: r.region, pose });
    });

    ctx.setTransform(dpr * k, 0, 0, dpr * k, dpr * (w / 2 - cam.x * k), dpr * (h / 2 - cam.y * k));
    regionPoses.forEach(({ region, pose }) => {
      if (pose.a <= 0.01 || pose.R <= 1) return;
      const unassessed = region.id === UNASSESSED_REGION;
      const color = unassessed ? '#737373' : areaColor(region.color);
      ctx.globalAlpha = pose.a;
      const grad = ctx.createRadialGradient(pose.cx, pose.cy, 0, pose.cx, pose.cy, pose.R * 1.04);
      grad.addColorStop(0, hexAlpha(color, unassessed ? 0.06 : 0.13));
      grad.addColorStop(0.55, hexAlpha(color, unassessed ? 0.035 : 0.06));
      grad.addColorStop(1, hexAlpha(color, 0));
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(pose.cx, pose.cy, pose.R * 1.04, 0, Math.PI * 2);
      ctx.fill();

      // Score scale: faint rings at 75 / 50 / 25
      ctx.setLineDash([3 / k, 4 / k]);
      ctx.lineWidth = 1 / k;
      ctx.strokeStyle = 'rgba(10, 10, 10, 0.07)';
      [75, 50, 25].forEach((s) => {
        ctx.beginPath();
        ctx.arc(pose.cx, pose.cy, pose.scaleR * (1 - s / 100), 0, Math.PI * 2);
        ctx.stroke();
      });
      ctx.setLineDash([]);

      // Outer hairline and the share ring (top actors' share of influence weight)
      ctx.beginPath();
      ctx.arc(pose.cx, pose.cy, pose.R, 0, Math.PI * 2);
      ctx.strokeStyle = hexAlpha(color, unassessed ? 0.18 : 0.22);
      ctx.lineWidth = 1 / k;
      if (unassessed) ctx.setLineDash([5 / k, 5 / k]);
      ctx.stroke();
      ctx.setLineDash([]);
      const shares = p.regionMeta[region.id]?.shares || [];
      if (shares.length) {
        const ringR = pose.R + 5 / k;
        const gap = Math.min(0.06, 4 / (k * ringR));
        let angle = -Math.PI / 2;
        const alphas = [0.95, 0.7, 0.5, 0.36, 0.26];
        ctx.lineWidth = 3 / k;
        ctx.lineCap = 'butt';
        shares.slice(0, 5).forEach((share, i) => {
          const sweep = share * Math.PI * 2;
          if (sweep > gap * 1.5) {
            ctx.beginPath();
            ctx.arc(pose.cx, pose.cy, ringR, angle + gap / 2, angle + sweep - gap / 2);
            ctx.strokeStyle = hexAlpha(color, alphas[i]);
            ctx.stroke();
          }
          angle += sweep;
        });
      }
      ctx.globalAlpha = 1;
    });

    /* nodes */
    const poses: Array<{ n: AnimNode; pose: Pose }> = [];
    nodesRef.current.forEach((n, key) => {
      const pose = nodePose(n, now);
      if (now - n.start - n.delay < n.dur) animating = true;
      else if (n.leaving) {
        nodesRef.current.delete(key);
        return;
      }
      if (pose.r > 0.05 && pose.a > 0.01) poses.push({ n, pose });
    });

    const hovered = p.hoverId;
    const selected = p.selectedId;
    const compare = new Set(p.compareIds);
    const highlight = new Set(p.highlightIds);
    const byEntity = new Map<string, Array<{ n: AnimNode; pose: Pose }>>();
    poses.forEach((item) => {
      const list = byEntity.get(item.n.entityId) || [];
      list.push(item);
      byEntity.set(item.n.entityId, list);
    });

    // Ties of the selected entity, drawn from its strongest instance
    if (selected && p.ties.length) {
      const own = (byEntity.get(selected) || []).slice().sort((a, b) => b.n.score - a.n.score)[0];
      if (own) {
        ctx.lineWidth = 1 / k;
        ctx.strokeStyle = 'rgba(10, 10, 10, 0.16)';
        p.ties.forEach((id) => {
          const other = (byEntity.get(id) || []).slice().sort((a, b) => b.n.score - a.n.score)[0];
          if (!other) return;
          ctx.beginPath();
          ctx.moveTo(own.pose.x, own.pose.y);
          ctx.lineTo(other.pose.x, other.pose.y);
          ctx.stroke();
        });
      }
    }

    // The same entity across areas
    const linkInstances = (id: string | null, stroke: string, dashed: boolean) => {
      if (!id) return;
      const list = byEntity.get(id);
      if (!list || list.length < 2) return;
      ctx.lineWidth = 1.25 / k;
      ctx.strokeStyle = stroke;
      ctx.setLineDash(dashed ? [4 / k, 4 / k] : []);
      for (let i = 0; i < list.length - 1; i++) {
        const a = list[i].pose;
        const b = list[i + 1].pose;
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const len = Math.hypot(dx, dy) || 1;
        const bend = Math.min(60, len * 0.12);
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.quadraticCurveTo(mx - (dy / len) * bend, my + (dx / len) * bend, b.x, b.y);
        ctx.stroke();
      }
      ctx.setLineDash([]);
    };
    linkInstances(selected, `rgba(${ACCENT_RGB}, 0.45)`, false);
    if (hovered !== selected) linkInstances(hovered, 'rgba(10, 10, 10, 0.32)', true);

    const pulse = highlight.size ? (Math.sin((now - highlightAtRef.current) / 260) + 1) / 2 : 0;
    if (highlight.size && !p.reducedMotion && now - highlightAtRef.current < 5000) animating = true;

    poses.sort((a, b) => b.pose.r - a.pose.r);
    poses.forEach(({ n, pose }) => {
      const entity = p.entityById.get(n.entityId);
      const org = entity?.type === 'organization';
      const isSel = n.entityId === selected;
      const isHover = n.entityId === hovered;
      const isCompare = compare.has(n.entityId);
      const isHighlight = highlight.has(n.entityId);
      let alpha = pose.a;
      if (hovered && !isHover && !isSel) alpha *= 0.25;
      else if (!hovered && p.matchIds && !p.matchIds.has(n.entityId)) alpha *= 0.14;
      ctx.globalAlpha = alpha;
      const unassessed = n.regionId === UNASSESSED_REGION;
      const fill = nodeFill(n.color, n.score, unassessed);
      const { x, y, r } = pose;

      if (isHighlight) {
        // Many references at once stay readable: the halo gets quieter as the set grows
        const strength = Math.min(1, 6 / highlight.size);
        shapePath(ctx, org, x, y, r + (6 + 5 * (p.reducedMotion ? 0.5 : pulse)) / k);
        ctx.fillStyle = `rgba(${ACCENT_RGB}, ${(0.08 + 0.14 * (p.reducedMotion ? 0.5 : pulse)) * (0.45 + 0.55 * strength)})`;
        ctx.fill();
      }
      if (isSel) {
        shapePath(ctx, org, x, y, r + 8 / k);
        ctx.fillStyle = `rgba(${ACCENT_RGB}, 0.12)`;
        ctx.fill();
      }

      shapePath(ctx, org, x, y, r);
      ctx.fillStyle = `rgb(${fill[0]}, ${fill[1]}, ${fill[2]})`;
      ctx.fill();
      ctx.lineWidth = 1.25 / k;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.stroke();
      if (unassessed) {
        ctx.setLineDash([2 / k, 2 / k]);
        ctx.strokeStyle = 'rgba(115, 115, 115, 0.6)';
        ctx.lineWidth = 1 / k;
        shapePath(ctx, org, x, y, r - 0.5 / k);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      if (isSel || isCompare || isHover) {
        shapePath(ctx, org, x, y, r + 2.5 / k);
        ctx.lineWidth = (isSel ? 2 : 1.5) / k;
        ctx.strokeStyle = isSel ? `rgb(${ACCENT_RGB})` : isCompare ? '#0a0a0a' : unassessed ? '#525252' : readableOn(areaColor(n.color));
        ctx.stroke();
      }

      // Score inside the node when there is room
      if (r * k >= 10.5 && !unassessed) {
        ctx.save();
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.font = `500 ${Math.min(12, Math.max(9, r * k * 0.72))}px ${FONT}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = luminance(fill) > 0.62 ? 'rgba(10, 10, 10, 0.78)' : '#ffffff';
        ctx.fillText(String(Math.round(n.score)), sx(x), sy(y) + 0.5);
        ctx.restore();
      }
    });
    ctx.globalAlpha = 1;

    /* screen-space labels */
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    regionPoses.forEach(({ region, pose }) => {
      if (pose.a <= 0.05) return;
      const unassessed = region.id === UNASSESSED_REGION;
      const top = sy(pose.cy - pose.R) - 14;
      // Centred over the area but kept on the canvas: on a phone an area near the edge is narrower than its label
      const inside = (text: string) => {
        const half = ctx.measureText(text).width / 2 + 6;
        return half * 2 >= w ? w / 2 : Math.min(Math.max(sx(pose.cx), half), w - half);
      };
      ctx.globalAlpha = pose.a;
      ctx.font = `500 13px ${FONT}`;
      ctx.lineJoin = 'round';
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      const labelX = inside(region.label);
      ctx.strokeText(region.label, labelX, top - 14);
      ctx.fillStyle = unassessed ? '#737373' : readableOn(areaColor(region.color));
      ctx.fillText(region.label, labelX, top - 14);
      const sub = p.regionMeta[region.id]?.sub;
      if (sub) {
        ctx.font = `400 11px ${FONT}`;
        const subX = inside(sub);
        ctx.strokeText(sub, subX, top);
        ctx.fillStyle = '#a3a3a3';
        ctx.fillText(sub, subX, top);
      }
      // Scale hint when zoomed in enough to read it
      if (pose.scaleR * k > 150 && !unassessed) {
        ctx.font = `400 10px ${FONT}`;
        ctx.fillStyle = 'rgba(115, 115, 115, 0.7)';
        [75, 50, 25].forEach((s) => ctx.fillText(String(s), sx(pose.cx), sy(pose.cy - pose.scaleR * (1 - s / 100)) - 3));
      }
      ctx.globalAlpha = 1;
    });

    if (poses.length) {
      const focus = new Set<string>();
      if (selected) focus.add(selected);
      if (hovered) focus.add(hovered);
      compare.forEach((id) => focus.add(id));
      highlight.forEach((id) => focus.add(id));
      const zoomedIn = k >= 1.25;

      // Screen-space discs in a coarse grid, so labels avoid covering nodes
      const CELL = 48;
      const grid = new Map<string, number[]>();
      const discs = poses.map(({ pose }) => ({ x: sx(pose.x), y: sy(pose.y), r: pose.r * k + 1 }));
      discs.forEach((d, i) => {
        for (let gx = Math.floor((d.x - d.r) / CELL); gx <= Math.floor((d.x + d.r) / CELL); gx++) {
          for (let gy = Math.floor((d.y - d.r) / CELL); gy <= Math.floor((d.y + d.r) / CELL); gy++) {
            const key = `${gx},${gy}`;
            const list = grid.get(key);
            if (list) list.push(i);
            else grid.set(key, [i]);
          }
        }
      });
      const coversNode = (rect: number[], own: number) => {
        for (let gx = Math.floor(rect[0] / CELL); gx <= Math.floor(rect[2] / CELL); gx++) {
          for (let gy = Math.floor(rect[1] / CELL); gy <= Math.floor(rect[3] / CELL); gy++) {
            const list = grid.get(`${gx},${gy}`);
            if (!list) continue;
            for (let q = 0; q < list.length; q++) {
              if (list[q] === own) continue;
              const d = discs[list[q]];
              const cx = Math.max(rect[0], Math.min(d.x, rect[2]));
              const cy = Math.max(rect[1], Math.min(d.y, rect[3]));
              if ((cx - d.x) ** 2 + (cy - d.y) ** 2 < d.r * d.r) return true;
            }
          }
        }
        return false;
      };

      const rankInRegion = new Map<string, number>();
      const ordered = poses
        .map((item, index) => ({ ...item, index }))
        .sort((a, b) => b.n.score - a.n.score)
        .map((item) => {
          const rank = rankInRegion.get(item.n.regionId) || 0;
          rankInRegion.set(item.n.regionId, rank + 1);
          return { ...item, rank };
        })
        .filter(({ n, pose, rank }) => {
          if (focus.has(n.entityId)) return true;
          if (!labelsRef.current || hovered) return false;
          if (p.matchIds) return p.matchIds.has(n.entityId);
          if (zoomedIn) return pose.r * k >= 4;
          return rank < 3 && n.regionId !== UNASSESSED_REGION;
        })
        .sort((a, b) => {
          const pa = a.n.entityId === selected || a.n.entityId === hovered ? 3 : focus.has(a.n.entityId) ? 2 : a.rank === 0 ? 1 : 0;
          const pb = b.n.entityId === selected || b.n.entityId === hovered ? 3 : focus.has(b.n.entityId) ? 2 : b.rank === 0 ? 1 : 0;
          return pb - pa || b.n.score - a.n.score;
        })
        .slice(0, 260);

      const placed: number[][] = [];
      const overlapsLabel = (rect: number[]) => placed.some((q) => rect[0] < q[2] && rect[2] > q[0] && rect[1] < q[3] && rect[3] > q[1]);
      ctx.textBaseline = 'middle';
      const labelled = new Set<string>();
      ordered.forEach(({ n, pose, index, rank }) => {
        const entity = p.entityById.get(n.entityId);
        if (!entity) return;
        // Zoomed out, name each entity once (its strongest position)
        if (!zoomedIn && labelled.has(n.entityId)) return;
        const isFocus = focus.has(n.entityId);
        // Selection and hover always get a label; other focus (AI references, compare) only where it fits
        const pinned = n.entityId === selected || n.entityId === hovered || (rank === 0 && !p.matchIds && !highlight.size);
        const text = truncate(entity.label, isFocus ? 34 : 22);
        const font = `${isFocus || rank === 0 ? 500 : 400} 11px ${FONT}`;
        ctx.font = font;
        const cacheKey = `${font}|${text}`;
        let tw = labelWidthCache.current.get(cacheKey);
        if (tw === undefined) {
          tw = ctx.measureText(text).width;
          labelWidthCache.current.set(cacheKey, tw);
        }
        const d = discs[index];
        if (d.x < -120 || d.x > w + 120 || d.y < -40 || d.y > h + 40) return;
        const half = tw / 2 + 5;
        // below, above, right, left
        const spots = [
          [d.x - half, d.y + d.r + 3, d.x + half, d.y + d.r + 19],
          [d.x - half, d.y - d.r - 19, d.x + half, d.y - d.r - 3],
          [d.x + d.r + 3, d.y - 8, d.x + d.r + 3 + half * 2, d.y + 8],
          [d.x - d.r - 3 - half * 2, d.y - 8, d.x - d.r - 3, d.y + 8],
        ];
        const inside = (r: number[]) => r[0] >= 4 && r[2] <= w - 4 && r[1] >= 4 && r[3] <= h - 4;
        let rect = spots.find((r) => inside(r) && !overlapsLabel(r) && !coversNode(r, index));
        if (!rect) {
          if (!pinned) return;
          rect = spots.find((r) => inside(r) && !overlapsLabel(r)) || spots.find(inside) || spots[0];
        }
        placed.push(rect);
        labelled.add(n.entityId);
        const cx = (rect[0] + rect[2]) / 2;
        const cy = (rect[1] + rect[3]) / 2;
        ctx.globalAlpha = pose.a;
        // Soft pill so labels stay legible over nodes and region tints
        ctx.beginPath();
        const rr = 8;
        ctx.moveTo(rect[0] + rr, rect[1]);
        ctx.arcTo(rect[2], rect[1], rect[2], rect[3], rr);
        ctx.arcTo(rect[2], rect[3], rect[0], rect[3], rr);
        ctx.arcTo(rect[0], rect[3], rect[0], rect[1], rr);
        ctx.arcTo(rect[0], rect[1], rect[2], rect[1], rr);
        ctx.closePath();
        ctx.fillStyle = n.entityId === selected ? 'rgba(254, 242, 242, 0.96)' : 'rgba(255, 255, 255, 0.88)';
        ctx.fill();
        if (isFocus) {
          ctx.lineWidth = 1;
          ctx.strokeStyle = n.entityId === selected ? 'rgba(234, 42, 51, 0.35)' : 'rgba(10, 10, 10, 0.1)';
          ctx.stroke();
        }
        ctx.textAlign = 'center';
        ctx.fillStyle = n.entityId === selected ? '#b91c1c' : isFocus || rank === 0 ? '#0a0a0a' : '#525252';
        ctx.fillText(text, cx, cy + 0.5);
      });
      ctx.globalAlpha = 1;
    }

    if (animating && live.current.active) frameRef.current = window.requestAnimationFrame(draw);
  }, []);

  const requestDraw = useCallback(() => {
    if (frameRef.current == null && typeof window !== 'undefined') frameRef.current = window.requestAnimationFrame(draw);
  }, [draw]);

  useEffect(
    () => () => {
      if (frameRef.current != null) window.cancelAnimationFrame(frameRef.current);
      // StrictMode remounts: a stale id would block every future frame
      frameRef.current = null;
    },
    [],
  );

  /* ---------- camera ---------- */
  const flyTo = useCallback(
    (to: Camera, dur = 760) => {
      const target = { ...to, k: clampZoom(to.k) };
      if (live.current.reducedMotion || !live.current.active) {
        flightRef.current = null;
        cameraRef.current = target;
      } else {
        flightRef.current = { from: { ...cameraRef.current }, to: target, start: performance.now(), dur };
      }
      setCard(null);
      requestDraw();
    },
    [requestDraw],
  );

  const cameraFor = useCallback((intent: CameraIntent, w: number, h: number): Camera | null => {
    const l = layoutRef.current;
    if (!l || !intent || !w || !h) return null;
    if (intent.kind === 'fit') return fitCamera(l.bounds, w, h, w < 500 ? 20 : 56);
    if (intent.kind === 'region') {
      const region = l.regions.find((r) => r.id === intent.id);
      if (!region) return null;
      return fitCamera(
        { minX: region.cx - region.R, maxX: region.cx + region.R, minY: region.cy - region.R - 44, maxY: region.cy + region.R + 8 },
        w,
        h,
        28,
      );
    }
    const wanted = new Set(intent.ids);
    const list = l.instances.filter((i) => wanted.has(i.entityId));
    if (!list.length) return null;
    if (list.length === 1) return { x: list[0].x, y: list[0].y, k: Math.max(cameraRef.current.k, 1.6) };
    const cam = fitCamera(
      {
        minX: Math.min(...list.map((i) => i.x - 60)),
        maxX: Math.max(...list.map((i) => i.x + 60)),
        minY: Math.min(...list.map((i) => i.y - 60)),
        maxY: Math.max(...list.map((i) => i.y + 60)),
      },
      w,
      h,
      40,
    );
    return { ...cam, k: Math.min(2.2, cam.k) };
  }, []);

  const frame = useCallback(
    (intent: CameraIntent) => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const cam = cameraFor(intent, wrap.clientWidth, wrap.clientHeight);
      if (!cam) return;
      intentRef.current = intent;
      flyTo(cam);
    },
    [cameraFor, flyTo],
  );

  const fit = useCallback(() => frame({ kind: 'fit' }), [frame]);
  const focusEntities = useCallback((ids: string[]) => frame({ kind: 'entities', ids }), [frame]);
  const focusEntity = useCallback((id: string) => frame({ kind: 'entities', ids: [id] }), [frame]);
  const focusRegion = useCallback((id: string) => frame({ kind: 'region', id }), [frame]);

  useImperativeHandle(ref, () => ({ fit, focusEntity, focusEntities, focusRegion }), [fit, focusEntity, focusEntities, focusRegion]);

  /* ---------- layout → animations ---------- */
  useEffect(() => {
    if (!layout) return;
    const now = performance.now();
    const reduced = live.current.reducedMotion;
    const first = !layoutRef.current;
    const prevRegionIds = new Set((layoutRef.current?.regions || []).map((r) => r.id));
    layoutRef.current = layout;

    const regionById = new Map<string, MapRegion>();
    layout.regions.forEach((r) => regionById.set(r.id, r));

    // Regions
    const nextRegions = new Map<string, AnimRegion>();
    layout.regions.forEach((region) => {
      const existing = regionsRef.current.get(region.id);
      const to: RegionPose = { cx: region.cx, cy: region.cy, R: region.R, scaleR: region.scaleR, a: 1 };
      const from: RegionPose = existing
        ? regionPose(existing, now)
        : { cx: region.cx, cy: region.cy, R: region.R * 0.6, scaleR: region.scaleR * 0.6, a: 0 };
      nextRegions.set(region.id, { region, from: reduced ? to : from, to, start: now, dur: reduced ? 1 : 700, leaving: false });
    });
    regionsRef.current.forEach((r, id) => {
      if (nextRegions.has(id)) return;
      const pose = regionPose(r, now);
      nextRegions.set(id, { region: r.region, from: pose, to: { ...pose, R: pose.R * 0.8, a: 0 }, start: now, dur: reduced ? 1 : 420, leaving: true });
    });
    regionsRef.current = nextRegions;

    // Nodes: bloom out from the region centre on entry, glide on change, shrink on exit
    const nextNodes = new Map<string, AnimNode>();
    layout.instances.forEach((inst) => {
      const region = regionById.get(inst.regionId)!;
      const existing = nodesRef.current.get(inst.key);
      const to: Pose = { x: inst.x, y: inst.y, r: inst.r, a: 1 };
      const from: Pose = existing ? nodePose(existing, now) : { x: region.cx, y: region.cy, r: 0, a: 0 };
      const dist = Math.hypot(inst.x - region.cx, inst.y - region.cy);
      nextNodes.set(inst.key, {
        key: inst.key,
        entityId: inst.entityId,
        regionId: inst.regionId,
        score: inst.score,
        color: region.color,
        from: reduced ? to : from,
        to,
        start: now,
        delay: reduced || existing ? 0 : (first ? 120 : 0) + (dist / Math.max(1, region.R)) * 260,
        dur: reduced ? 1 : NODE_DUR,
        leaving: false,
      });
    });
    nodesRef.current.forEach((n, key) => {
      if (nextNodes.has(key)) return;
      const pose = nodePose(n, now);
      nextNodes.set(key, { ...n, from: pose, to: { ...pose, r: 0, a: 0 }, start: now, delay: 0, dur: reduced ? 1 : 360, leaving: true });
    });
    nodesRef.current = nextNodes;

    const regionsChanged =
      layout.regions.length !== prevRegionIds.size || layout.regions.some((r) => !prevRegionIds.has(r.id));
    const wrap = wrapRef.current;
    if (wrap && (first || (regionsChanged && intentRef.current?.kind === 'fit'))) {
      const cam = fitCamera(layout.bounds, wrap.clientWidth, wrap.clientHeight, wrap.clientWidth < 500 ? 20 : 56);
      if (first) cameraRef.current = cam;
      else flyTo(cam, 700);
    }
    requestDraw();
  }, [layout, flyTo, requestDraw]);

  // Keep the framed subject framed when the viewport resizes (panels opening, rotation)
  useEffect(() => {
    if (!size.w || !layoutRef.current) return;
    const cam = cameraFor(intentRef.current, size.w, size.h);
    if (cam) {
      if (flightRef.current) flightRef.current = { ...flightRef.current, to: cam };
      else cameraRef.current = cam;
    }
    requestDraw();
  }, [size, cameraFor, requestDraw]);

  useEffect(() => {
    highlightAtRef.current = performance.now();
    requestDraw();
  }, [highlightIds, requestDraw]);

  useEffect(() => {
    requestDraw();
  }, [selectedId, hoverId, compareIds, matchIds, ties, showLabels, regionMeta, entityById, active, requestDraw]);

  /* ---------- interaction ---------- */
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const gesture = useRef<{ startX: number; startY: number; cam: Camera; moved: boolean; pinch?: { dist: number; cx: number; cy: number } } | null>(null);
  const lastTap = useRef<{ t: number; id: string | null }>({ t: 0, id: null });

  const toWorld = (px: number, py: number) => {
    const wrap = wrapRef.current!;
    const cam = cameraRef.current;
    return { x: cam.x + (px - wrap.clientWidth / 2) / cam.k, y: cam.y + (py - wrap.clientHeight / 2) / cam.k };
  };

  const hitTest = (px: number, py: number): { entityId: string; regionId: string; x: number; y: number; r: number } | null => {
    const { x, y } = toWorld(px, py);
    const now = performance.now();
    const k = cameraRef.current.k;
    let best: { entityId: string; regionId: string; x: number; y: number; r: number } | null = null;
    let bestD = Infinity;
    nodesRef.current.forEach((n) => {
      if (n.leaving) return;
      const pose = nodePose(n, now);
      const d = Math.hypot(pose.x - x, pose.y - y);
      const reach = pose.r + Math.max(2, 6 / k);
      if (d <= reach && d - pose.r < bestD) {
        bestD = d - pose.r;
        best = { entityId: n.entityId, regionId: n.regionId, x: pose.x, y: pose.y, r: pose.r };
      }
    });
    return best;
  };

  const localPoint = (event: { clientX: number; clientY: number }) => {
    const rect = wrapRef.current!.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const showCardFor = (hit: { entityId: string; regionId: string; x: number; y: number; r: number } | null) => {
    if (!hit) {
      setCard(null);
      return;
    }
    const wrap = wrapRef.current!;
    const cam = cameraRef.current;
    setCard({
      id: hit.entityId,
      regionId: hit.regionId,
      sx: (hit.x - cam.x) * cam.k + wrap.clientWidth / 2,
      sy: (hit.y - cam.y) * cam.k + wrap.clientHeight / 2 - hit.r * cam.k,
    });
  };

  const onPointerDown = (event: React.PointerEvent) => {
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    const pt = localPoint(event);
    pointers.current.set(event.pointerId, pt);
    if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      gesture.current = {
        startX: pt.x,
        startY: pt.y,
        cam: { ...cameraRef.current },
        moved: true,
        pinch: { dist: Math.hypot(a.x - b.x, a.y - b.y), cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 },
      };
    } else {
      gesture.current = { startX: pt.x, startY: pt.y, cam: { ...cameraRef.current }, moved: false };
    }
    flightRef.current = null;
  };

  const onPointerMove = (event: React.PointerEvent) => {
    const pt = localPoint(event);
    const g = gesture.current;
    if (pointers.current.has(event.pointerId) && g) {
      pointers.current.set(event.pointerId, pt);
      const wrap = wrapRef.current!;
      if (g.pinch && pointers.current.size >= 2) {
        const [a, b] = Array.from(pointers.current.values());
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        const k = clampZoom(g.cam.k * (dist / Math.max(1, g.pinch.dist)));
        const cx = (a.x + b.x) / 2;
        const cy = (a.y + b.y) / 2;
        const wx = g.cam.x + (g.pinch.cx - wrap.clientWidth / 2) / g.cam.k;
        const wy = g.cam.y + (g.pinch.cy - wrap.clientHeight / 2) / g.cam.k;
        cameraRef.current = { k, x: wx - (cx - wrap.clientWidth / 2) / k, y: wy - (cy - wrap.clientHeight / 2) / k };
        intentRef.current = null;
        requestDraw();
        return;
      }
      const dx = pt.x - g.startX;
      const dy = pt.y - g.startY;
      if (!g.moved && Math.hypot(dx, dy) > 4) {
        g.moved = true;
        setCard(null);
        if (live.current.hoverId) onHover(null);
      }
      if (g.moved) {
        cameraRef.current = { ...g.cam, x: g.cam.x - dx / g.cam.k, y: g.cam.y - dy / g.cam.k };
        intentRef.current = null;
        requestDraw();
      }
      return;
    }
    if (event.pointerType !== 'mouse') return;
    const hit = hitTest(pt.x, pt.y);
    const id = hit?.entityId || null;
    if (id !== live.current.hoverId) onHover(id);
    if ((hit?.entityId || null) !== card?.id || hit?.regionId !== card?.regionId) showCardFor(hit);
    if (canvasRef.current) canvasRef.current.style.cursor = hit ? 'pointer' : 'grab';
  };

  const onPointerUp = (event: React.PointerEvent) => {
    const g = gesture.current;
    pointers.current.delete(event.pointerId);
    if (pointers.current.size > 0) return;
    gesture.current = null;
    if (!g || g.moved || event.type === 'pointercancel') return;
    const pt = localPoint(event);
    const hit = hitTest(pt.x, pt.y);
    const id = hit?.entityId || null;
    const now = performance.now();
    if (id && lastTap.current.id === id && now - lastTap.current.t < 320) {
      lastTap.current = { t: 0, id: null };
      onOpen(id);
      return;
    }
    lastTap.current = { t: now, id };
    onSelect(id);
    if (event.pointerType !== 'mouse') showCardFor(null);
  };

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = wrap.getBoundingClientRect();
      const px = event.clientX - rect.left;
      const py = event.clientY - rect.top;
      const cam = cameraRef.current;
      const factor = Math.exp(-event.deltaY * (event.ctrlKey ? 0.012 : 0.0022));
      const k = clampZoom(cam.k * factor);
      const wx = cam.x + (px - rect.width / 2) / cam.k;
      const wy = cam.y + (py - rect.height / 2) / cam.k;
      flightRef.current = null;
      cameraRef.current = { k, x: wx - (px - rect.width / 2) / k, y: wy - (py - rect.height / 2) / k };
      intentRef.current = null;
      setCard(null);
      requestDraw();
    };
    wrap.addEventListener('wheel', onWheel, { passive: false });
    return () => wrap.removeEventListener('wheel', onWheel);
  }, [requestDraw]);

  const zoomBy = (factor: number) => {
    intentRef.current = null;
    flyTo({ ...cameraRef.current, k: cameraRef.current.k * factor }, 320);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const step = 60 / cameraRef.current.k;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    if (moves[event.key]) {
      event.preventDefault();
      const [dx, dy] = moves[event.key];
      intentRef.current = null;
      flyTo({ ...cameraRef.current, x: cameraRef.current.x + dx, y: cameraRef.current.y + dy }, 220);
    } else if (event.key === '+' || event.key === '=') {
      zoomBy(1.4);
    } else if (event.key === '-') {
      zoomBy(1 / 1.4);
    }
  };

  const cardEntity = card ? entityById.get(card.id) : null;
  const wrapW = wrapRef.current?.clientWidth || 0;

  return (
    <div className="absolute inset-0">
      <div
        ref={wrapRef}
        className="absolute inset-0 touch-none select-none overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-600/30"
        tabIndex={0}
        role="img"
        aria-label={`Power map: ${layout?.regions.length || 0} areas, ${layout?.instances.length || 0} positions. Use the Matrix lens for a readable table.`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={(event) => {
          if (event.pointerType === 'mouse' && !gesture.current) {
            setCard(null);
            if (live.current.hoverId) onHover(null);
          }
        }}
        onKeyDown={onKeyDown}
      >
        <canvas ref={canvasRef} className="block cursor-grab" />
      </div>

      {cardEntity && card && (
        <div
          className="pointer-events-none absolute z-10 w-[220px] rounded-xl bg-white/95 p-2.5 shadow-[0_12px_32px_-12px_rgba(0,0,0,0.25)] ring-1 ring-black/[0.06] backdrop-blur-sm infl-rise"
          style={{
            left: Math.max(8, Math.min(card.sx - 110, wrapW - 228)),
            // bottom anchoring (not a transform) so the entrance animation can't displace it
            ...(card.sy > 150 ? { bottom: size.h - (card.sy - 10) } : { top: card.sy + 30 }),
          }}
        >
          <p className="truncate text-[13px] leading-tight text-ink">{cardEntity.label}</p>
          <p className="mt-0.5 truncate text-[11.5px] text-neutral-500">{cardEntity.subtitle}</p>
          {cardEntity.assessed ? (
            <ul className="mt-2 space-y-1">
              {cardEntity.scores.slice(0, 5).map((s) => (
                <li key={s.categoryId} className={`flex items-center gap-1.5 text-[11.5px] ${s.categoryId === card.regionId ? 'text-ink' : 'text-neutral-500'}`}>
                  <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: areaColor(s.color) }} />
                  <span className="min-w-0 flex-1 truncate">{s.label}</span>
                  <span className="h-1 w-12 overflow-hidden rounded-full bg-neutral-100">
                    <span className="block h-full rounded-full" style={{ width: `${s.score}%`, backgroundColor: areaColor(s.color) }} />
                  </span>
                  <span className="w-5 text-right tabular-nums">{s.score}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1.5 text-[11.5px] text-neutral-500">
              Unassessed · network position <span className="tabular-nums text-ink">{Math.round(nodesRef.current.get(`${card.id}:${UNASSESSED_REGION}`)?.score || 0)}</span>
            </p>
          )}
        </div>
      )}

      {/* Legend */}
      {size.w >= 720 && (
      <div className="pointer-events-none absolute bottom-3 left-3 flex items-center gap-3 whitespace-nowrap rounded-full bg-white/85 px-3 py-1.5 text-[11px] text-neutral-500 ring-1 ring-black/[0.05] backdrop-blur-sm">
        <span className="flex items-center gap-1.5">
          <svg width="22" height="10" viewBox="0 0 22 10" aria-hidden="true">
            <circle cx="5" cy="5" r="4" fill="#a3a3a3" />
            <rect x="13" y="1" width="8" height="8" rx="2.5" fill="#a3a3a3" />
          </svg>
          Person · Org
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="26" height="12" viewBox="0 0 26 12" aria-hidden="true">
            <circle cx="6" cy="6" r="5" fill="none" stroke="#d4d4d4" strokeDasharray="2 2" />
            <circle cx="6" cy="6" r="1.8" fill="#737373" />
            <circle cx="19" cy="6" r="3" fill="#d4d4d4" />
            <circle cx="19" cy="6" r="5.5" fill="none" stroke="#e5e5e5" />
          </svg>
          Closer to centre · stronger
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M7 1.5 A5.5 5.5 0 0 1 12.5 7" fill="none" stroke="#737373" strokeWidth="2.5" />
            <path d="M12.3 8.6 A5.5 5.5 0 0 1 3 11" fill="none" stroke="#d4d4d4" strokeWidth="2.5" />
          </svg>
          Ring · share of top actors
        </span>
      </div>
      )}

      {/* Camera controls */}
      <div
        className={`absolute right-3 flex flex-col gap-0.5 rounded-full bg-white/90 p-1 shadow-[0_6px_20px_-10px_rgba(0,0,0,0.25)] ring-1 ring-black/[0.06] backdrop-blur-sm ${
          dockSpace ? 'bottom-[68px]' : 'bottom-3'
        }`}
      >
        {[
          { label: 'Zoom in', icon: Plus, onClick: () => zoomBy(1.5) },
          { label: 'Zoom out', icon: Minus, onClick: () => zoomBy(1 / 1.5) },
          { label: 'Fit (F)', icon: Scan, onClick: fit },
        ].map(({ label, icon: Icon, onClick }) => (
          <button
            key={label}
            type="button"
            aria-label={label}
            title={label}
            onClick={onClick}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
          >
            <Icon className="h-4 w-4" strokeWidth={1.75} />
          </button>
        ))}
        <button
          type="button"
          aria-label={showLabels ? 'Hide labels' : 'Show labels'}
          aria-pressed={showLabels}
          title={showLabels ? 'Hide labels' : 'Show labels'}
          onClick={() => setShowLabels((v) => !v)}
          className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
            showLabels ? 'text-ink' : 'text-neutral-400'
          }`}
        >
          <Tags className="h-4 w-4" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
});
